import {
  type DetailedOrganizationRes,
  OrganizationType,
  rdbV2OrganizationsAllTimeCommitsDetail,
  type RdbV2OrganizationsAllTimeCommitsDetailParams,
  type RosterCompResAllTimeCommitsRelatedResRelatedPagedData,
  type SportFilter,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/index';
import { baseSports } from '@on3/ui-lib/store/mocks/sports';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import {
  getHighSchoolSportLabel,
  getIdFromParam,
  getSportKeyFromParam,
} from '@on3/ui-lib/utils/url';
import { FullFormerAthletesList } from '@public/components/HighSchool/FullFormerAthletesList';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import {
  createCanonicalUrl,
  createDescription,
  createTitle,
} from '@public/utils/meta';
import { type AxiosError } from 'axios';
import { type GetServerSideProps } from 'next';
import { usePathname } from 'next/navigation';

export interface ITopRecruitsPageProps extends IGenericPageProps {
  orgInfo?: DetailedOrganizationRes;
  allTimeCommits?: RosterCompResAllTimeCommitsRelatedResRelatedPagedData;
  filters: {
    sports?: SportFilter[];
  };
}

const TopRecruitsPage = ({
  siteData,
  allTimeCommits,
  filters,
  ...props
}: ITopRecruitsPageProps) => {
  const { relatedModel, list } = allTimeCommits ?? {};
  const { sport, organization } = relatedModel ?? {};
  const sportLabel = sport ? getHighSchoolSportLabel(sport) : '';
  const { name } = organization || {};

  const description = createDescription(
    'Discover the top {0} recruits who graduated from {1}. Explore detailed profiles of {1} alumni who have excelled in {0} and achieved remarkable success. Stay updated with On3.',
    sportLabel,
    name,
  );

  const pathname = usePathname();
  const canonical = createCanonicalUrl(pathname);

  const pageTitle = createTitle(
    `Top`,
    sportLabel,
    'Alumni from',
    name,
    ' - On3',
  );
  const heading = createTitle(name, 'Top', sportLabel, 'Alumni');

  const noIndex = !list || list?.length < 3;

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      noIndex={noIndex}
      siteData={siteData}
      title={pageTitle}
    >
      <FullFormerAthletesList
        allTimeCommits={allTimeCommits}
        filters={filters}
        key={`${relatedModel?.sport?.name}`}
        title={heading}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const highSchoolSiteKey = 402;
  const siteData = await withValidUrl.isValid(ctx, highSchoolSiteKey);
  const userData = await withAuthentication.isAuth(ctx);
  const { sport = 'football', id } = ctx.query as { id: string; sport: string };

  const orgKey = getIdFromParam(id);
  const sportKey = getSportKeyFromParam(sport);

  if (!siteData || !orgKey || !sportKey) {
    return {
      notFound: true,
    };
  }

  const params: RdbV2OrganizationsAllTimeCommitsDetailParams = {
    sportKey,
    organizationKey: orgKey,
  };

  try {
    const [allTimeCommits] = await Promise.allSettled([
      rdbV2OrganizationsAllTimeCommitsDetail(proxyApi, params),
    ]);

    const alumniData = getFulfilledValue(allTimeCommits);

    const { relatedModel } = alumniData || {};

    const orgType = relatedModel?.organization?.orgTypeEnum;
    const orgSlug = relatedModel?.organization?.urlSlug;

    if (orgType !== OrganizationType.HighSchool) {
      return { notFound: true };
    }

    if (id !== orgSlug) {
      return {
        redirect: {
          permanent: true,
          destination: `/high-school/${orgSlug}/${sport}/alumni/`,
        },
      };
    }

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || [],
      allTimeCommits: alumniData,
      filters: {
        sports: baseSports,
      },
    };

    return { props: { ...data } };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.error(err);

    return { props: {} };
  }
};

export default withValidUrl(TopRecruitsPage);
